import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  fetchTeacherAttendanceSelfRange,
  teacherSelfCheckIn,
  teacherSelfCheckOut,
} from "./api/teacherHrApi";
import { endOfDayISO, startOfDayISO } from "./helpers";
import "./teacherHr.css";

/* -------------------- helpers -------------------- */

function sameCalendarDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function pickTodayRecord(records) {
  const now = new Date();
  if (!Array.isArray(records)) return null;
  return records.find((r) => r.date && sameCalendarDay(r.date, now)) || null;
}

function formatTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
}

function formatDateLong(date) {
  return new Date(date).toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/* Safely read location info (it's an OBJECT in the API) */
function describeLocation(loc) {
  if (!loc) return null;
  if (typeof loc === "string") return loc; // defensive
  const parts = [];
  if (loc.validationMethod) parts.push(loc.validationMethod);
  if (loc.validationStatus) parts.push(loc.validationStatus);
  return parts.length ? parts.join(" • ") : null;
}

/* Only these color keys are safe to use with MUI `${color}.lighter` syntax */
const VALID_PALETTE_COLORS = [
  "primary",
  "secondary",
  "success",
  "error",
  "warning",
  "info",
];

/* Normalize PascalCase / snake_case / spaces into a single key */
function normalizeStatusKey(status) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const STATUS_MAP = {
  present: {
    color: "success",
    icon: <CheckCircleIcon fontSize="small" />,
    label: "Present",
  },
  absent: {
    color: "error",
    icon: <CancelIcon fontSize="small" />,
    label: "Absent",
  },
  half_day: {
    color: "warning",
    icon: <AccessTimeIcon fontSize="small" />,
    label: "Half Day",
  },
  halfday: {
    color: "warning",
    icon: <AccessTimeIcon fontSize="small" />,
    label: "Half Day",
  },
  late: {
    color: "warning",
    icon: <ScheduleIcon fontSize="small" />,
    label: "Late",
  },
  leave: {
    color: "info",
    icon: <CalendarIcon fontSize="small" />,
    label: "Leave",
  },
  on_leave: {
    color: "info",
    icon: <CalendarIcon fontSize="small" />,
    label: "On Leave",
  },
  paid_leave: {
    color: "info",
    icon: <CalendarIcon fontSize="small" />,
    label: "Paid Leave",
  },
};

function statusConfig(status) {
  const key = normalizeStatusKey(status);
  const found = STATUS_MAP[key];
  if (found) return found;
  return {
    color: "default",
    icon: <AccessTimeIcon fontSize="small" />,
    label: status || "Unknown",
  };
}

/* -------------------- component -------------------- */

export default function AttendanceToday() {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [records, setRecords] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const from = startOfDayISO(new Date());
    const to = endOfDayISO(new Date());
    try {
      const res = await fetchTeacherAttendanceSelfRange({ from, to });
      const data = res?.data?.data;
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load attendance",
      );
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const todayRow = pickTodayRecord(records);

  const normalized = normalizeStatusKey(todayRow?.status);
  const isLeave =
    normalized === "leave" ||
    normalized === "on_leave" ||
    normalized === "paid_leave";

  const hasCheckIn = Boolean(todayRow?.checkInTime);
  const hasCheckOut = Boolean(todayRow?.checkOutTime);

  const canCheckIn = !isLeave && !hasCheckIn;
  const canCheckOut = !isLeave && hasCheckIn && !hasCheckOut;

  const onCheckIn = async () => {
    setActing(true);
    try {
      await teacherSelfCheckIn();
      toast.success("Check-in recorded successfully");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Check-in failed",
      );
    } finally {
      setActing(false);
    }
  };

  const onCheckOut = async () => {
    setActing(true);
    try {
      await teacherSelfCheckOut({});
      toast.success("Check-out recorded successfully");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Check-out failed",
      );
    } finally {
      setActing(false);
    }
  };

  const status = statusConfig(todayRow?.status);
  const safeColor = VALID_PALETTE_COLORS.includes(status.color)
    ? status.color
    : null;

  const checkInTime = formatTime(todayRow?.checkInTime);
  const checkOutTime = formatTime(todayRow?.checkOutTime);

  const checkInLocationText = describeLocation(todayRow?.checkInLocation);
  const checkOutLocationText = describeLocation(todayRow?.checkOutLocation);

  const durationText = (() => {
    if (!checkInTime) return "—";
    if (!checkOutTime) return "In progress";
    const start = new Date(todayRow.checkInTime).getTime();
    const end = new Date(todayRow.checkOutTime).getTime();
    const diff = Math.max(0, Math.round((end - start) / 60000));
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h ${m}m`;
  })();

  const note = todayRow?.remark || todayRow?.reason;

  /* -------------------- render -------------------- */

  return (
    <div
      className="container-2xl p-2 orange-scrollBar"
      style={{ maxHeight: 620, overflow: "auto" }}
    >
      {/* Header */}
      <Box className="mb-4">
        <Box className="d-flex align-items-center gap-3 mb-2">
          <ScheduleIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" component="h1" className="fw-bold m-0">
              Today's Attendance
            </Typography>
            <Typography variant="body2" color="text.secondary" className="mt-1">
              {formatDateLong(new Date())}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Status Card */}
      <Card className="shadow-sm border rounded-3 mb-4">
        <CardContent className="p-4">
          {loading ? (
            <Box className="d-flex flex-column gap-3">
              <Skeleton
                variant="rectangular"
                height={80}
                className="rounded-3"
              />
              <Skeleton
                variant="rectangular"
                height={120}
                className="rounded-3"
              />
            </Box>
          ) : (
            <>
              {/* Status Banner */}
              <Box
                className="rounded-3 p-4 mb-4 text-center"
                sx={{
                  backgroundColor:
                    todayRow && safeColor ? `${safeColor}.lighter` : "grey.50",
                  border: 1,
                  borderColor:
                    todayRow && safeColor ? `${safeColor}.light` : "grey.200",
                }}
              >
                <Box className="d-flex align-items-center justify-content-center gap-2 mb-2">
                  {todayRow ? status.icon : <AccessTimeIcon color="disabled" />}
                  <Chip
                    size="medium"
                    label={todayRow ? status.label : "Not Checked In"}
                    color={todayRow && safeColor ? safeColor : "default"}
                    className="fw-bold"
                    sx={{ fontSize: "1rem", py: 0.5 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {!todayRow
                    ? "You haven't checked in yet today"
                    : isLeave
                      ? "You are on leave today"
                      : checkOutTime
                        ? "Your attendance is complete for today"
                        : "You are currently checked in"}
                </Typography>
              </Box>

              {/* Timeline */}
              {todayRow && (
                <Box className="mb-4">
                  <Typography
                    variant="subtitle2"
                    className="text-uppercase text-muted fw-bold mb-3"
                  >
                    Activity Timeline
                  </Typography>
                  <Box className="position-relative ps-4">
                    {/* Vertical line */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 15,
                        top: 8,
                        bottom: 8,
                        width: 2,
                        backgroundColor: "divider",
                      }}
                    />

                    {/* Check-in */}
                    <Box className="d-flex align-items-start gap-3 mb-4 position-relative">
                      <Box
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: checkInTime
                            ? "success.main"
                            : "grey.300",
                          color: "white",
                          zIndex: 1,
                        }}
                      >
                        <LoginIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="body1" className="fw-semibold">
                          Check In
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {checkInTime || "Not recorded"}
                        </Typography>
                        {checkInLocationText && (
                          <Box className="d-flex align-items-center gap-1 mt-1">
                            <LocationIcon fontSize="small" color="action" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {checkInLocationText}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Check-out */}
                    <Box className="d-flex align-items-start gap-3 position-relative">
                      <Box
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: checkOutTime
                            ? "secondary.main"
                            : "grey.300",
                          color: "white",
                          zIndex: 1,
                        }}
                      >
                        <LogoutIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="body1" className="fw-semibold">
                          Check Out
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {checkOutTime || "Pending"}
                        </Typography>
                        {checkOutLocationText && (
                          <Box className="d-flex align-items-center gap-1 mt-1">
                            <LocationIcon fontSize="small" color="action" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {checkOutLocationText}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Action Buttons */}
              <Box className="d-flex flex-wrap gap-3">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  disabled={!canCheckIn || acting}
                  onClick={onCheckIn}
                  className="text-capitalize fw-semibold px-4"
                  color="success"
                >
                  {acting && canCheckIn ? "Processing…" : "Check In"}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LogoutIcon />}
                  disabled={!canCheckOut || acting}
                  onClick={onCheckOut}
                  className="text-capitalize fw-semibold px-4"
                  color="secondary"
                >
                  {acting && canCheckOut ? "Processing…" : "Check Out"}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={acting}
                  className="text-capitalize fw-semibold px-4"
                >
                  Refresh
                </Button>
              </Box>

              {/* Additional Info */}
              {note && (
                <Box
                  className="mt-4 p-3 rounded-3"
                  sx={{
                    backgroundColor: "info.lighter",
                    border: 1,
                    borderColor: "info.light",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    className="fw-bold text-info mb-1"
                  >
                    Note
                  </Typography>
                  <Typography variant="body2">{note}</Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      {!loading && todayRow && (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <CardContent className="p-3 text-center">
                <AccessTimeIcon color="action" className="mb-1" />
                <Typography
                  variant="caption"
                  className="text-uppercase text-muted fw-bold d-block"
                >
                  Duration
                </Typography>
                <Typography variant="h6" className="fw-bold">
                  {durationText}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <CardContent className="p-3 text-center">
                <ScheduleIcon color="action" className="mb-1" />
                <Typography
                  variant="caption"
                  className="text-uppercase text-muted fw-bold d-block"
                >
                  Status
                </Typography>
                <Typography variant="h6" className="fw-bold">
                  {status.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <CardContent className="p-3 text-center">
                <LoginIcon color="action" className="mb-1" />
                <Typography
                  variant="caption"
                  className="text-uppercase text-muted fw-bold d-block"
                >
                  Check In
                </Typography>
                <Typography variant="h6" className="fw-bold text-success">
                  {checkInTime || "—"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <CardContent className="p-3 text-center">
                <LogoutIcon color="action" className="mb-1" />
                <Typography
                  variant="caption"
                  className="text-uppercase text-muted fw-bold d-block"
                >
                  Check Out
                </Typography>
                <Typography variant="h6" className="fw-bold text-secondary">
                  {checkOutTime || "—"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </div>
  );
}
